import queryString, { ParsedQuery, ParseOptions } from "query-string";
import { useHistory, useLocation } from "react-router";
import * as H from "history";

/**
 * Here is a bunch of query param helper methods and the root logic on how to interact with the url
 * in a consistent typesafe manner.  The hooks allow for some degree of flexibility, so url params can be grabbed
 * on at a time, serialized as objects, or grab multiple at once.
 *
 * The functions rely on generic `string` union types to check for consistent key usage.
 *
 * unsafe:
 *  const [value] = useQueryParam('foo')
 *
 * safe:
 *   type Foo = 'foo' | 'bar'
 *  const [value] = useQueryParam<Foo>('foo')
 *  const [value] = useQueryParam<Foo>('foo2') //fails to compile
 *
 *  While this in a bit of extra typing for one page, it has benefits for things like shared filter values
 *  allowing links and redirects to also check params exist and are used.
 */
type Param = string | boolean | number;
export type QParam = Param | Param[];
type UseQueryParamDefaults<TParam> = [
  TParam,
  (paramsObj: TParam, evaluateOnDemand?: boolean) => void,
  (evaluateOnDemand?: boolean) => void
];
type UseQueryParamReturn<TParam> = [
  TParam | null,
  (paramsObj: TParam, evaluateOnDemand?: boolean) => void,
  (evaluateOnDemand?: boolean) => void
];

/**
 * Our basic set of options, we want all values parsed to runtime types.
 */
export const qsOptions: ParseOptions = {
  parseNumbers: true,
  parseBooleans: true,
  arrayFormat: "index"
};
export function stringify(
  a: { [key: string]: any },
  opts: ParseOptions = qsOptions
): string {
  const querySafe = Object.keys(a).reduce(
    (acc: { [key: string]: string | number | boolean }, key) => {
      const entry = a[key];
      if (typeof entry === "object" && !Array.isArray(entry)) {
        acc[key] = JSON.stringify(entry);
      } else {
        acc[key] = entry;
      }
      return acc;
    },
    {}
  );
  return queryString.stringify(querySafe, opts);
}
export function parse(
  search: string | undefined,
  options = qsOptions
): ParsedQuery<string | boolean | number> {
  if (!search) {
    return {};
  }
  return queryString.parse(search, options);
}

/**
 * Grabs the whole query and returns it as an object.
 * String, numbers, bools, and arrays are parsed out.
 * NOTE: Object values are not parsed out, and remain as json string.
 */
export function useQueryArgs(
  parseOptions = qsOptions
): ParsedQuery<string | boolean | number> {
  let location = useLocation();
  return parse(location.search, parseOptions);
}

/**
 * Returns a specific field from the query string, provides a set and clear method on that field as well.
 * String, numbers, bools, and arrays are parsed out.
 * NOTE: Object values are not parsed out, and remain as json string.
 * @param name
 * @param parseOptions
 */
export function useQueryParam<V extends string>(
  name: V,
  parseOptions = qsOptions
): UseQueryParamReturn<QParam> {
  const history = useHistory();
  let query = useQueryArgs(parseOptions);
  let hash = history.location.hash;
  const set = (paramsObj: QParam, evaluateOnDemand = false) => {
    if (evaluateOnDemand) {
      query = parse(history.location.search, parseOptions);
    }
    if (paramsObj) {
      query[name] = paramsObj;
    } else {
      delete query[name];
    }
    history.replace({
      search: stringify(query),
      hash: hash
    });
  };
  const clear = (evaluateOnDemand = false) => {
    if (evaluateOnDemand) {
      query = parse(history.location.search, parseOptions);
    }
    delete query[name];
    history.replace({
      search: stringify(query),
      hash: hash
    });
  };
  const value = query[name];
  return [value || null, set, clear];
}

/**
 * Following is a set of typesafe methods to grab params.
 * TS generics were slightly limited, so had to write out some overload boiler plate.
 * string,number,bool, string[],number[],bool[],
 */

export function useQueryParamString<V extends string>(
  name: V
): UseQueryParamReturn<string> {
  const [value, set, clear] = useQueryParam(name, {
    ...qsOptions,
    parseNumbers: false
  });

  if (value) {
    return [value.toString(), set, clear];
  }

  return [null, set, clear];
}
export function useQueryParamNumber<V extends string>(
  name: V
): UseQueryParamReturn<number> {
  const [value, set, clear] = useQueryParam(name);
  if (typeof value === "number") {
    return [value, set, clear];
  }
  return [null, set, clear];
}
export function useQueryParamBoolean<V extends string>(
  name: V
): UseQueryParamReturn<boolean> {
  const [value, set, clear] = useQueryParam(name);
  if (typeof value === "boolean") {
    return [value, set, clear];
  }
  return [null, set, clear];
}

export function useQueryParamStringArray<V extends string>(
  name: V
): UseQueryParamReturn<string[]> {
  const [values, set, clear] = useQueryParam(name);
  if (values instanceof Array) {
    return [
      values.filter((it): it is string => typeof it === "string"),
      set,
      clear
    ];
  }
  return [null, set, clear];
}
export function useQueryParamNumberArray<V extends string>(
  name: V
): UseQueryParamReturn<number[]> {
  const [values, set, clear] = useQueryParam(name);
  if (values instanceof Array) {
    return [
      values.filter((it): it is number => typeof it === "number"),
      set,
      clear
    ];
  }
  return [null, set, clear];
}
export function useQueryParamBooleanArray<V extends string>(
  name: V
): UseQueryParamReturn<boolean[]> {
  const [values, set, clear] = useQueryParam(name);
  if (values instanceof Array) {
    return [
      values.filter((it): it is boolean => typeof it === "boolean"),
      set,
      clear
    ];
  }
  return [null, set, clear];
}

export function keepRecordValues(
  names: string[],
  query: ParsedQuery<string | boolean | number>
): Record<string, QParam> {
  return names.reduce((v: Record<string, QParam>, key: string) => {
    const q = query[key];
    if (q) {
      v[key] = q;
    }
    return v;
  }, {});
}

export type FlatParamMap = Record<string, QParam>;

/**
 * Set and retrieve multiple keys in one go.
 * @param names
 */
export function useQueryParamMap<V extends string>(
  names: Array<V>
): UseQueryParamReturn<FlatParamMap> {
  const history = useHistory();
  let query = useQueryArgs();
  let hash = history.location.hash;
  const set = (paramsObj: Record<string, QParam>) => {
    const newQ = {
      ...query,
      ...paramsObj
    };
    history.replace({
      search: stringify(newQ),
      hash: hash
    });
  };
  const clear = () => {
    names.forEach(name => {
      delete query[name];
    });
    history.replace({
      search: stringify(query),
      hash: hash
    });
  };
  const value = keepRecordValues(names, query);
  return [value || null, set, clear];
}

export function useQueryParamMapWith<V extends string>(
  names: Array<V>,
  defaultValue: Record<string, QParam>
): UseQueryParamReturn<FlatParamMap> {
  const [value, set, clear] = useQueryParamMap(names);
  if (value) {
    return [value, set, clear];
  }
  return [defaultValue, set, clear];
}

/**
 * Allows you to easily get and set object values from the query string.
 * @param name
 * @param defaultValue the default if there is no matching value in the url;
 */
export function useQueryParamObjectWith<V extends string, T>(
  name: V,
  defaultValue: T
): UseQueryParamDefaults<T> {
  const [value, set, clear] = useQueryParamObject<V, T>(name);
  if (value) {
    return [value, set, clear];
  }
  return [defaultValue, set, clear];
}
export const parseJsonQueryParam = JSON.parse;
export function getQueryObject<V extends string, T>(
  location: H.Location
): (name: V) => T | null {
  const evaluateOnDemand = (name: V): T | null => {
    const q = parse(location.search);
    const v = q[name];
    if (typeof v === "string") {
      return parseJsonQueryParam(v) as T;
    }
    return null;
  };
  return evaluateOnDemand;
}
export function useQueryParamObject<V extends string, T>(
  name: V
): UseQueryParamReturn<T> {
  const [value, set, clear] = useQueryParam(name);
  const setObj = (v: T | T[]) => {
    const sValue = JSON.stringify(v);
    if (sValue !== "{}") {
      set(sValue);
    } else {
      clear();
    }
  };
  if (typeof value === "string") {
    try {
      const T = parseJsonQueryParam(value);
      return [T, setObj, clear];
    } catch (e) {
      return [null, setObj, clear];
    }
  }
  return [null, setObj, clear];
}
